import React from 'react';
import { Gavel as RuleIcon } from '@mui/icons-material';
import { Box, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';

import { SimpleMarkdownRenderer } from '@components/Markdown/SimpleMarkdownRenderer';
import type { ComplianceRule } from '../types';

interface ComplianceRuleCardProps {
  rule: ComplianceRule;
}

export const ComplianceRuleCard: React.FC<ComplianceRuleCardProps> = ({ rule }) => {
  return (
    <Card
      sx={{
        width: 300,
        minWidth: 300,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 1,
          borderColor: 'borderColors.brand',
        },
      }}
    >
      {/* Header Section with Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <RuleIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              color: 'textColors.darkText',
              mb: 0.25,
            }}
          >
            {rule.title || rule.selector_text || rule.rule_text || 'Untitled Rule'}
          </Typography>
          {/* Display facets as chips */}
          {(rule.facets?.payer?.length || rule.facets?.cpt?.length || rule.facets?.provider_type?.length) && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.25}>
              {rule.facets.payer?.map((payer) => (
                <Chip
                  key={payer}
                  label={payer}
                  size="small"
                  sx={{ height: 14, fontSize: '0.625rem' }}
                  variant="outlined"
                />
              ))}
              {rule.facets.cpt?.map((cpt) => (
                <Chip
                  key={cpt}
                  label={`CPT ${cpt}`}
                  size="small"
                  sx={{ height: 14, fontSize: '0.625rem' }}
                  variant="outlined"
                />
              ))}
              {rule.facets.provider_type?.map((providerType) => (
                <Chip
                  key={providerType}
                  label={providerType}
                  size="small"
                  sx={{ height: 14, fontSize: '0.625rem' }}
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
          {/* Fallback to legacy payer_type if facets not available */}
          {!rule.facets?.payer?.length && rule.payer_type && (
          <Typography variant="caption" sx={{ color: 'textColors.lightText', fontSize: '0.6875rem' }}>
            {rule.payer_type === 'specific' ? rule.applicable_payer_name : 'General Requirement'}
          </Typography>
          )}
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        <Stack spacing={1}>
          {/* Body Markdown - Use SimpleMarkdownRenderer for lightweight rendering */}
          {rule.body_md ? (
            <SimpleMarkdownRenderer markdown={rule.body_md} />
          ) : (
            // Fallback to legacy fields if body_md not available
            <>
              {rule.rule_text && (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
                  {rule.rule_text}
                </Typography>
              )}
              {rule.criterion && (
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 600,
                color: 'textColors.lightText',
                fontSize: '0.625rem',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                display: 'block',
                mb: 0.25,
              }}
            >
              Criterion
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
              {rule.criterion}
            </Typography>
          </Box>
              )}
              {rule.compliance_rationale && (
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 600,
                color: 'textColors.lightText',
                fontSize: '0.625rem',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                display: 'block',
                mb: 0.25,
              }}
            >
              Rationale
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
              {rule.compliance_rationale}
            </Typography>
            </Box>
          )}
            </>
          )}
        </Stack>
      </CardContent>

      {/* Footer with Source Information */}
      {(rule.source?.document_title || rule.source?.document_date || rule.source_document) && (
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'backgroundColors.secondary',
          borderTop: '1px solid',
          borderColor: 'borderColors.primary',
        }}
      >
        <Typography variant="caption" sx={{ color: 'textColors.lightText', fontSize: '0.6875rem', fontStyle: 'italic' }}>
            Source: <strong style={{ color: 'inherit' }}>{rule.source?.document_title || rule.source_document || 'Unknown'}</strong>
            {(rule.source?.document_date || rule.source_date) && ` • ${rule.source?.document_date || rule.source_date}`}
        </Typography>
      </Box>
      )}
    </Card>
  );
};

